import React, { useState } from 'react'
import { Button } from './ui/Button'
import {Shield, FileText, Check} from 'lucide-react'
import { lumi } from '../lib/lumi'
import toast from 'react-hot-toast'

interface LGPDConsentModalProps {
  userId: string
  onConsent: () => void
}

export function LGPDConsentModal({ userId, onConsent }: LGPDConsentModalProps) {
  const [acceptedPrivacy, setAcceptedPrivacy] = useState(false)
  const [acceptedTerms, setAcceptedTerms] = useState(false)
  const [loading, setLoading] = useState(false)

  const handleAccept = async () => {
    if (!acceptedPrivacy || !acceptedTerms) {
      toast.error('Você deve aceitar ambos os documentos para continuar')
      return
    }

    setLoading(true)
    try {
      const timestamp = new Date().toISOString()
      const ipAddress = 'anonymized' // In production, get from backend

      // Record privacy policy consent
      await lumi.entities.lgpd_consents.create({
        userId,
        consentType: 'privacy_policy',
        accepted: true,
        timestamp,
        ipAddress,
        documentVersion: 'v1.0'
      })

      // Record terms of use consent
      await lumi.entities.lgpd_consents.create({
        userId,
        consentType: 'terms_of_use',
        accepted: true,
        timestamp,
        ipAddress,
        documentVersion: 'v1.0'
      })

      // Update profile with consent flag
      const { list } = await lumi.entities.profiles.list({
        filter: { userId }
      })

      if (list.length > 0) {
        await lumi.entities.profiles.update(list[0]._id, {
          lgpdConsentGiven: true,
          lgpdConsentDate: timestamp
        })
      }

      toast.success('Consentimento registrado com sucesso!')
      onConsent()
    } catch (error) {
      console.error('Failed to record consent:', error)
      toast.error('Erro ao registrar consentimento')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          {/* Header */}
          <div className="text-center mb-6">
            <div className="flex justify-center mb-4">
              <div className="w-16 h-16 rounded-full bg-red-600 flex items-center justify-center">
                <Shield className="h-8 w-8 text-white" />
              </div>
            </div>
            <h2 className="text-2xl font-bold text-gray-900">Bem-vindo à Doe!</h2>
            <p className="text-sm text-gray-600 mt-2">
              Antes de continuar, precisamos do seu consentimento
            </p>
          </div>

          {/* Content */}
          <div className="space-y-4 mb-6">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h3 className="font-bold text-gray-900 mb-2 flex items-center">
                <Shield className="h-4 w-4 mr-2 text-blue-600" />
                Proteção de Dados (LGPD)
              </h3>
              <p className="text-sm text-gray-700 mb-3">
                Levamos sua privacidade a sério. Seus dados são utilizados apenas para conectar doadores e receptores de sangue, em conformidade com a Lei Geral de Proteção de Dados (LGPD).
              </p>
              <ul className="text-sm text-gray-600 space-y-1 ml-4">
                <li>✓ Seus dados não são vendidos a terceiros</li>
                <li>✓ Você pode solicitar exclusão a qualquer momento</li>
                <li>✓ Usamos criptografia para proteger informações sensíveis</li>
                <li>✓ Tipo sanguíneo é tratado como dado sensível de saúde</li>
              </ul>
            </div>

            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
              <h3 className="font-bold text-gray-900 mb-2">O que você está consentindo:</h3>
              <ul className="text-sm text-gray-700 space-y-2">
                <li className="flex items-start">
                  <Check className="h-4 w-4 mr-2 text-green-600 flex-shrink-0 mt-0.5" />
                  <span>Coleta e armazenamento de dados pessoais (nome, idade, tipo sanguíneo, localização, contato)</span>
                </li>
                <li className="flex items-start">
                  <Check className="h-4 w-4 mr-2 text-green-600 flex-shrink-0 mt-0.5" />
                  <span>Compartilhamento de seus dados com solicitantes compatíveis quando houver match</span>
                </li>
                <li className="flex items-start">
                  <Check className="h-4 w-4 mr-2 text-green-600 flex-shrink-0 mt-0.5" />
                  <span>Envio de notificações sobre solicitações de doação próximas a você</span>
                </li>
                <li className="flex items-start">
                  <Check className="h-4 w-4 mr-2 text-green-600 flex-shrink-0 mt-0.5" />
                  <span>Uso de cookies para melhorar sua experiência na plataforma</span>
                </li>
              </ul>
            </div>
          </div>

          {/* Checkboxes */}
          <div className="space-y-3 mb-6">
            <label className="flex items-start space-x-3 cursor-pointer">
              <input
                type="checkbox"
                checked={acceptedPrivacy}
                onChange={(e) => setAcceptedPrivacy(e.target.checked)}
                className="mt-1 rounded border-gray-300 text-red-600 focus:ring-red-500"
              />
              <span className="text-sm text-gray-700">
                Li e aceito a{' '}
                <a
                  href="/politica-privacidade"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-red-600 hover:underline font-medium"
                >
                  Política de Privacidade
                </a>
                {' '}(LGPD)
              </span>
            </label>

            <label className="flex items-start space-x-3 cursor-pointer">
              <input
                type="checkbox"
                checked={acceptedTerms}
                onChange={(e) => setAcceptedTerms(e.target.checked)}
                className="mt-1 rounded border-gray-300 text-red-600 focus:ring-red-500"
              />
              <span className="text-sm text-gray-700">
                Li e aceito os{' '}
                <a
                  href="/termos-uso"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-red-600 hover:underline font-medium"
                >
                  Termos de Uso
                </a>
              </span>
            </label>
          </div>

          {/* Actions */}
          <div className="flex flex-col sm:flex-row gap-3">
            <Button
              onClick={handleAccept}
              disabled={!acceptedPrivacy || !acceptedTerms || loading}
              className="flex-1 bg-red-600 hover:bg-red-700 text-white"
            >
              {loading ? 'Registrando...' : 'Aceitar e Continuar'}
            </Button>
            <Button
              variant="outline"
              onClick={() => window.location.href = '/'}
              disabled={loading}
              className="flex-1"
            >
              Recusar
            </Button>
          </div>

          <p className="text-xs text-gray-500 text-center mt-4">
            Ao aceitar, você confirma que tem pelo menos 16 anos de idade e concorda com o tratamento de seus dados conforme descrito.
          </p>
        </div>
      </div>
    </div>
  )
}
